import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { 
  ShoppingCart, 
  Package, 
  Clock, 
  Users, 
  Eye, 
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  Truck,
  AlertTriangle,
  Activity,
  DollarSign,
  BarChart3,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  ChevronLeft,
  Apple,
  Salad,
  Cookie,
  Soup,
  Cherry,
  Leaf
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart,
  Legend
} from "recharts";
import type { DashboardStats, Account, Product, Category, OrderWindowConfig } from "@shared/schema";
import { PRODUCT_CATEGORIES } from "@shared/schema";
import { GettingStartedChecklist } from "@/components/getting-started-checklist";
import { QbSyncedMark } from "@/components/qb-synced-mark";
import { canViewFinancials } from "@/lib/adminRole";
import { OrderSourceBadge } from "@/components/order-source-badge";
import { calculateOrderWindows, type OrderWindowConfig as WindowConfig } from "@shared/periods";

// Brand color palette (from the provided palette)
const BRAND_COLORS = {
  blue: "#19417D",
  green: "#467B5A", 
  brown: "#6B4F3B",
  orange: "#EA590C",
  yellow: "#FBBF24",
  skyBlue: "#385DF9",
  // Light versions
  lightBlue: "#BFDBFE",
  lightGreen: "#E2FBF3",
  lightBrown: "#E7E5E4",
  lightOrange: "#FBD8B0",
  lightYellow: "#FEF3C7",
  lightSkyBlue: "#F0F9FF"
};

// Chart colors using brand palette
const CHART_COLORS = {
  primary: BRAND_COLORS.blue,
  secondary: BRAND_COLORS.green, 
  warning: BRAND_COLORS.yellow,
  danger: BRAND_COLORS.orange,
  info: BRAND_COLORS.skyBlue,
  muted: BRAND_COLORS.brown
};

const STATUS_COLORS = {
  pending: '#BFDBFE',     // Light blue
  reviewed: '#F3D8B0',    // Light orange
  booked: '#F0F9FF',      // Very light blue
  procured: '#E2FBE8',    // Light green  
  invoiceDrafted: '#467B5A', // Brand green
  cancelled: BRAND_COLORS.orange
};

// Helper function to get category color (using only palette colors)
const getCategoryColor = (categoryName: string, index: number) => {
  const paletteColors = [
    '#BFDBFE',    // Light Blue (first)
    '#E2FBE8',    // Light Green (second)  
    '#F3D8B0',    // Light Orange (third)
    '#F0F9FF',    // Light Baby Blue (fourth)
    '#FEF3C7',    // Light Yellow (fifth)
    '#E7E5E4'     // Light Brown (sixth)
  ];
  
  // Use index-based cycling for consistent color order (blue, green, orange, baby blue, yellow, brown)
  return paletteColors[index % paletteColors.length];
};

// Helper function to truncate long category names
const truncateName = (name: string, maxLength: number = 20) => {
  return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
};

// Custom X-axis tick component for diagonal labels
const CustomXAxisTick = ({ x, y, payload }: any) => {
  if (!payload || !payload.value) return null;
  
  const truncatedName = truncateName(payload.value);
  
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill="#6B7280"
        fontSize="11"
        transform="rotate(-45)"
        className="select-none"
      >
        {truncatedName}
      </text>
    </g>
  );
};

// Helper function to get hover color (dark version)
const getHoverColor = (lightColor: string) => {
  const colorMap: Record<string, string> = {
    '#BFDBFE': '#19417D',    // Light Blue -> Dark Blue
    '#E2FBE8': '#467B5A',    // Light Green -> Dark Green  
    '#E7E5E4': '#6B4F3B',    // Light Gray -> Dark Brown
    '#F3D8B0': '#EA580C',    // Light Orange -> Dark Orange
    '#FEF3C7': '#FBBF24',    // Light Yellow -> Dark Yellow
    '#F0F9FF': '#38BDF9'     // Very Light Blue -> Dark Sky Blue
  };
  return colorMap[lightColor] || lightColor;
};

// Helper function to get solid brand color for status icons
const getSolidStatusColor = (lightColor: string) => {
  const statusSolidColors: Record<string, string> = {
    '#BFDBFE': '#19417D',    // Light Blue -> Solid Blue
    '#F3D8B0': '#EA580C',    // Light Orange -> Brand Dark Orange
    '#F0F9FF': '#38BDF9',    // Very Light Blue -> Solid Sky Blue
    '#E2FBE8': '#467B5A',    // Light Green -> Solid Green
    '#467B5A': '#467B5A'     // Already solid green -> stays solid green
  };
  return statusSolidColors[lightColor] || lightColor;
};

// Custom tooltip component for charts
const CustomTooltip = ({ active, payload, label, aggregationType }: any) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    
    // Format based on aggregation type
    let displayValue;
    if (aggregationType === 'value') {
      // Format as currency for Account Value
      displayValue = `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      // Regular item/order counting
      const dataKey = payload[0].dataKey;
      const unit = dataKey === 'items' ? 'items' : 'orders';
      displayValue = `${value} ${unit}`;
    }
    
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 transform transition-all duration-200 ease-in-out animate-in fade-in-0 zoom-in-95">
        <p className="font-semibold text-gray-800">
          <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: payload[0].color }}></span>
          {label}: {displayValue}
        </p>
      </div>
    );
  }
  return null;
};

// Custom tooltip component for pie chart (with percentage)
const CustomPieTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    // Calculate percentage from the payload data
    const percentage = data.payload.percent ? (data.payload.percent * 100).toFixed(1) : 
      data.payload.value && data.payload.totalValue ? 
        ((data.payload.value / data.payload.totalValue) * 100).toFixed(1) : '0';
    
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 transform transition-all duration-200 ease-in-out animate-in fade-in-0 zoom-in-95">
        <p className="font-semibold text-gray-800">
          <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: data.color }}></span>
          {data.name}: {data.value} orders ({percentage}%)
        </p>
        {data.payload.isOther && data.payload.otherCategories && (
          <div className="mt-2 text-xs text-gray-600">
            <p className="font-medium">Includes:</p>
            {data.payload.otherCategories.map((cat: any, index: number) => (
              <p key={index} className="ml-2">
                • {cat.name}: {cat.orders} orders ({cat.percentage.toFixed(1)}%)
              </p>
            ))}
          </div>
        )}
      </div>
    );
  }
  return null;
};

export default function AdminDashboard() {
  const [selectedPeriod, setSelectedPeriod] = useState("previous");
  const [aggregationType, setAggregationType] = useState<'items' | 'variety' | 'value'>('items');
  // QuickBooks revenue ("Account Value") is admin-only; the server 403s staff.
  const showFinancials = canViewFinancials();
  const [activeBar, setActiveBar] = useState<number | null>(null);
  const [activeSlice, setActiveSlice] = useState<number | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Function to scroll the chart horizontally to the right
  const scrollChart = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = 240; // Scroll by ~3 bars (80px each)
      container.scrollLeft += scrollAmount;
    }
  };

  // Function to scroll the chart horizontally to the left
  const scrollChartLeft = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = 240; // Scroll by ~3 bars (80px each)
      container.scrollLeft -= scrollAmount;
    }
  };

  // Handle scroll position tracking
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      setScrollPosition(scrollContainerRef.current.scrollLeft);
    }
  };


  // Fetch dashboard statistics
  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: [`/api/dashboard/stats?period=${selectedPeriod}`]
  });

  // Pipeline stage counts over every order in the selected period, counted by the
  // server. Keyed under "/api/orders" so the order and procurement mutations that
  // invalidate ["/api/orders"] refresh it, as they did when it was built from that list.
  const { data: pipelineCounts } = useQuery<{ pending: number; reviewed: number; booked: number; procured: number; billed: number }>({
    queryKey: ["/api/orders", "dashboard-pipeline", { period: selectedPeriod }],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/dashboard/pipeline?period=${selectedPeriod}`);
      return res.json();
    },
  });

  // Lightweight recent activity query — no items needed for display
  const { data: recentOrders = [] } = useQuery<any[]>({
    queryKey: ["/api/orders", { limit: 10, includeItems: false }],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/orders?limit=10&includeItems=false");
      return res.json();
    },
  });

  // Fetch QuickBooks authentication status
  const { data: quickbooksAuthStatus } = useQuery<{ isAuthenticated: boolean }>({
    queryKey: ['/api/quickbooks/auth-status'],
    staleTime: 0, // Always fetch fresh auth status - no caching for authentication
    gcTime: 0, // Don't cache auth status results
  });

  // Fetch order window configuration for the organization
  const { data: windowConfig } = useQuery<OrderWindowConfig>({
    queryKey: ["/api/order-window-config"],
  });

  // Determine if windows are enabled
  const windowsEnabled = windowConfig?.enabled ?? false;

  // Calculate window labels for display - show different labels when windows enabled vs disabled
  const windowLabels = useMemo(() => {
    if (!windowsEnabled || !windowConfig) {
      // When windows disabled, show calendar-based labels
      return { current: 'This Week', previous: 'Last Week' };
    }
    
    const config: WindowConfig = {
      enabled: windowConfig.enabled,
      frequency: windowConfig.frequency as 'daily' | 'weekly' | 'biweekly' | 'custom',
      timezone: windowConfig.timezone || 'America/Toronto',
      anchorStart: new Date(windowConfig.anchorStart || Date.now()),
      cutoffHour: windowConfig.cutoffHour ?? 17,
      cutoffMinute: windowConfig.cutoffMinute ?? 0,
      customDays: windowConfig.customDays ?? 0,
    };
    
    const windows = calculateOrderWindows(config);
    return {
      current: windows.current.label,
      previous: windows.previous.label,
    };
  }, [windowConfig, windowsEnabled]);

  // Simple QuickBooks lifetime values using the exact same method as working individual accounts
  const { data: quickbooksAccountValue, error: quickbooksError, isLoading: quickbooksLoading } = useQuery<Array<{name: string, items: number}>>({  
    queryKey: [`/api/dashboard/quickbooks-lifetime-values?period=${selectedPeriod}`], // Pass period for date filtering
    enabled: showFinancials,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: 1,
    throwOnError: false,
  });

  // Fetch account performance based on selected aggregation type
  const { data: accountPerformanceData } = useQuery<Array<{name: string, items: number}>>({
    queryKey: [`/api/dashboard/account-performance?period=${selectedPeriod}&aggregationType=${aggregationType}`],
    enabled: aggregationType !== 'value', // Don't fetch if we're using the preloaded QuickBooks data
  });
  
  // Use the appropriate data based on aggregation type
  const accountPerformance = aggregationType === 'value' 
    ? (Array.isArray(quickbooksAccountValue) ? quickbooksAccountValue : [])
    : (Array.isArray(accountPerformanceData) ? accountPerformanceData : []);

  // Handle QuickBooks data format for Account Value display
  // When successful, accountPerformance will contain customer revenue data

  // Preload QuickBooks data in priority order when dashboard loads
  useEffect(() => {
    // Only preload if QuickBooks is authenticated and we're showing account values
    if (showFinancials && quickbooksAuthStatus?.isAuthenticated && aggregationType === 'value') {
      // Priority order: previous, current, this quarter, this year, all time
      const periodsToPreload = ['previous', 'current', 'thisquarter', 'thisyear', 'alltime'];
      
      // Delay each prefetch slightly to avoid overwhelming the server
      periodsToPreload.forEach((period, index) => {
        setTimeout(() => {
          queryClient.prefetchQuery({
            queryKey: [`/api/dashboard/quickbooks-lifetime-values?period=${period}`],
            staleTime: 5 * 60 * 1000, // Same cache settings as main query
            gcTime: 30 * 60 * 1000,
          });
        }, index * 3000); // 3000ms delay between each prefetch
      });
    }
  }, [showFinancials, quickbooksAuthStatus?.isAuthenticated, aggregationType]); // Only re-run if auth status or aggregation type changes

  // Fetch category performance for pie chart
  const { data: categoryPerformance } = useQuery<Array<{name: string, orders: number}>>({
    queryKey: [`/api/dashboard/category-performance?period=${selectedPeriod}`]
  });

  // Fetch account concentration data
  const { data: accountConcentration } = useQuery<{ 
    concentrationPercentage: number; 
    topAccountsCount: number; 
    topAccountNames: string[]; 
    totalItems: number; 
  }>({
    queryKey: [`/api/dashboard/active-accounts?period=${selectedPeriod}`]
  });

  // Pipeline stages (GET /dashboard/pipeline): Billed = sent to QuickBooks; otherwise
  // Procured / Booked roll up the aggregate procurement of the order's lines, and
  // Reviewed / Pending follow the status.
  const pipelineData = (() => {
    const { pending = 0, reviewed = 0, booked = 0, procured = 0, billed: invoiceDrafted = 0 } = pipelineCounts ?? {};

    const stages = [
      { stage: 'Pending', count: pending, icon: Clock, color: '#BFDBFE', iconColor: 'text-[#19417D]' },
      { stage: 'Reviewed', count: reviewed, icon: Eye, color: '#F3D8B0', iconColor: 'text-[#EA580C]' },
      { stage: 'Booked', count: booked, icon: Package, color: '#F0F9FF', iconColor: 'text-[#38BDF9]' },
      { stage: 'Procured', count: procured, icon: CheckCircle, color: '#E2FBE8', iconColor: 'text-[#467B5A]' },
      { stage: 'Billed', count: invoiceDrafted, icon: DollarSign, color: '#467B5A', iconColor: 'text-white' }
    ];
    
    // Find the furthest stage reached (highest index with orders)
    const furthestStageIndex = stages.reduce((maxIndex, stage, index) => {
      return stage.count > 0 ? index : maxIndex;
    }, -1);
    
    return stages.map((stage, index) => ({
      ...stage,
      isReached: index <= furthestStageIndex,
      isActive: stage.count > 0
    }));
  })();

  // Calculate order status distribution for pie chart
  const orderStatusData = [
    { name: 'Pending', value: pipelineData[0].count, color: STATUS_COLORS.pending },
    { name: 'Reviewed', value: pipelineData[1].count, color: STATUS_COLORS.reviewed },
    { name: 'Booked', value: pipelineData[2].count, color: STATUS_COLORS.booked },
    { name: 'Procured', value: pipelineData[3].count, color: STATUS_COLORS.procured },
    { name: 'Billed', value: pipelineData[4].count, color: STATUS_COLORS.invoiceDrafted }
  ].filter(item => item.value > 0);

  // Calculate trend indicators
  const calculateTrend = (current: number, previous: number) => {
    if (previous === 0) return { percentage: 0, direction: 'neutral' };
    const percentage = Math.round(((current - previous) / previous) * 100);
    const direction = percentage > 0 ? 'up' : percentage < 0 ? 'down' : 'neutral';
    return { percentage: Math.abs(percentage), direction };
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 space-y-6">
      {/* Getting Started Checklist - temporarily disabled */}
      {/* <GettingStartedChecklist /> */}

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Orders Card */}
        <Card className="relative group border-2 border-transparent hover:border-[#467B5A] transition-all duration-200">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wide group-hover:text-[#467B5A] transition-colors duration-200">TOTAL ORDERS</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold group-hover:text-[#467B5A] transition-colors duration-200" data-testid="text-total-orders">
              {stats?.totalOrders || 0}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {selectedPeriod === 'previous' && `Across ${windowLabels.previous.toLowerCase()}`}
              {selectedPeriod === 'current' && `Across ${windowLabels.current.toLowerCase()}`}
              {selectedPeriod === 'quarter' && 'Across this quarter'}
              {selectedPeriod === 'year' && 'Across this year'}
              {selectedPeriod === 'alltime' && 'Across all time'}
            </div>
          </CardContent>
          <ShoppingCart className="absolute top-1/2 right-8 transform -translate-y-1/2 h-8 w-8 text-muted-foreground group-hover:text-[#467B5A] transition-colors duration-200" />
        </Card>

        {/* Revenue Card */}
        <Card className="relative group border-2 border-transparent hover:border-orange-600 transition-all duration-200">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wide group-hover:text-orange-600 transition-colors duration-200">TOTAL ITEMS</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold group-hover:text-orange-600 transition-colors duration-200" data-testid="text-total-items">
              {stats?.totalItems || 0}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              Across all orders
            </div>
          </CardContent>
          <Package className="absolute top-1/2 right-8 transform -translate-y-1/2 h-8 w-8 text-muted-foreground group-hover:text-orange-600 transition-colors duration-200" />
        </Card>

        {/* Average Order Card */}
        <Card className="relative group border-2 border-transparent hover:border-sky-500 transition-all duration-200">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wide group-hover:text-sky-500 transition-colors duration-200">AVERAGE ORDER</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold group-hover:text-sky-500 transition-colors duration-200" data-testid="text-average-order">
              {stats?.totalOrders ? Math.round((stats.totalItems || 0) / stats.totalOrders) : 0}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              Items per order on average
            </div>
          </CardContent>
          <ShoppingCart className="absolute top-1/2 right-8 transform -translate-y-1/2 h-8 w-8 text-muted-foreground group-hover:text-sky-500 transition-colors duration-200" />
        </Card>

        {/* Account Concentration Card */}
        <Card className="relative group border-2 border-transparent hover:border-[#FBBF24] transition-all duration-200">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wide group-hover:text-[#FBBF24] transition-colors duration-200">ACCOUNT CONCENTRATION</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold group-hover:text-[#FBBF24] transition-colors duration-200" data-testid="text-account-concentration">
              {accountConcentration?.concentrationPercentage || 0}%
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              Items sold to top 5 accounts
            </div>
          </CardContent>
          <Users className="absolute top-1/2 right-8 transform -translate-y-1/2 h-8 w-8 text-muted-foreground group-hover:text-[#FBBF24] transition-colors duration-200" />
        </Card>
      </div>
      {/* Order Pipeline Visualization */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Order Pipeline</CardTitle>
          </div>
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-52 bg-gray-100 hover:bg-gray-200 border border-gray-200" data-testid="select-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current">{windowsEnabled ? `Current (${windowLabels.current})` : windowLabels.current}</SelectItem>
              <SelectItem value="previous">{windowsEnabled ? `Previous (${windowLabels.previous})` : windowLabels.previous}</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="alltime">All Time</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {/* Progress Line Background - between icon centers - Hidden on mobile */}
            <div 
              className="absolute top-6 h-0.5 bg-gray-200 z-0 hidden md:block"
              style={{
                left: '10%',
                right: '10%'
              }}
            />
            
            {/* Segmented Progress Lines - Each segment uses the color of the stage it comes from - Hidden on mobile */}
            {pipelineData.map((stage, index) => {
              if (index === pipelineData.length - 1) return null; // No line after the last stage
              
              // Check if this stage has been passed through:
              // Either it has orders, OR any stage after it has orders (meaning orders passed through this stage)
              const hasOrdersInLaterStages = pipelineData.slice(index + 1).some(laterStage => laterStage.count > 0);
              const isStagePassedThrough = stage.count > 0 || hasOrdersInLaterStages;
              
              const segmentWidth = 80 / (pipelineData.length - 1); // Width of each segment as percentage
              const segmentLeft = 10 + (index * segmentWidth); // Starting position of this segment
              
              // Get the darker color for this stage if it's been passed through
              const segmentColor = isStagePassedThrough ? getSolidStatusColor(stage.color) : '#E5E7EB';
              
              return (
                <div 
                  key={`line-${index}`}
                  className="absolute top-6 h-0.5 z-10 transition-all duration-500 ease-out hidden md:block"
                  style={{
                    left: `${segmentLeft}%`,
                    width: `${segmentWidth}%`,
                    backgroundColor: segmentColor
                  }}
                />
              );
            })}
            
            {/* Pipeline Stages - More spacing on mobile */}
            <div className="flex items-center justify-between gap-4 md:gap-2 relative z-20">
              {pipelineData.map((stage, index) => {
                const isReached = stage.isReached;
                const isActive = stage.isActive;
                const furthestStageIndex = pipelineData.reduce((maxIndex, stage, index) => stage.count > 0 ? index : maxIndex, -1);
                const isFurthestStage = index === furthestStageIndex && isReached;
                
                return (
                  <div key={stage.stage} className="flex-1 relative">
                    <div className="text-center">
                      <div 
                        className="mx-auto w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center mb-1 md:mb-2 transition-all duration-500"
                        style={{
                          backgroundColor: !isReached ? '#E5E7EB' : (isFurthestStage ? getSolidStatusColor(stage.color) : stage.color),
                          border: isReached ? `2px solid ${getSolidStatusColor(stage.color)}` : 'none'
                        }}
                      >
                        <stage.icon className={`h-4 w-4 md:h-6 md:w-6 transition-all duration-500 ${
                          !isReached ? 'text-gray-400' :
                          isFurthestStage ? 'text-white' :
                          stage.iconColor
                        }`} />
                      </div>
                      <p className={`text-xs md:text-sm font-medium transition-all duration-500 ${
                        !isReached ? 'text-gray-400' :
                        isFurthestStage ? 'text-gray-900 font-semibold' :
                        'text-gray-900'
                      }`}>{stage.stage}</p>
                      <p className={`text-xl md:text-2xl font-bold mt-0.5 md:mt-1 transition-all duration-500 ${
                        !isReached ? 'text-gray-400' :
                        isFurthestStage ? 'text-gray-900 font-black' :
                        'text-gray-900'
                      }`} data-testid={`text-pipeline-${stage.stage.toLowerCase().replace(' ', '-')}`}>
                        {stage.count}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account Performance Chart */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>Account Data</CardTitle>
              </div>
              <Select value={aggregationType} onValueChange={(value) => {
                try {
                  setAggregationType(value as 'items' | 'variety' | 'value');
                } catch {
                  // setAggregationType is a useState setter — this catch is a no-op safety net
                }
              }}>
                <SelectTrigger className="w-40 bg-gray-100 hover:bg-gray-200 border border-gray-200" data-testid="select-aggregation-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="items">Items Ordered</SelectItem>
                  <SelectItem value="variety">Item Variety</SelectItem>
                  {showFinancials && <SelectItem value="value">Account Value</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="pl-2 pr-6 pt-[0px] pb-[0px]">
            {/* Show QuickBooks connection message if Account Value is selected but QB not authenticated or has error */}
            {aggregationType === 'value' && (!quickbooksAuthStatus?.isAuthenticated || quickbooksError) ? (
              <div className="h-[400px] flex flex-col items-center justify-center text-center p-8">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464m1.414 1.414L15.121 15.121m-4.243-4.243L15.121 15.121m-4.243-4.243L12 12" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  {quickbooksError ? 'QuickBooks Connection Error' : 'QuickBooks Connection Required'}
                </h3>
                <p className="text-gray-500 text-sm max-w-md">
                  {quickbooksError 
                    ? 'There was an error connecting to QuickBooks. Please check your connection in Settings and try again.'
                    : 'To view account values, please connect to QuickBooks in the Settings page. This will allow the system to pull billing data for revenue analysis.'
                  }
                </p>
              </div>
            ) : aggregationType === 'value' && quickbooksLoading ? (
              // Loading state for QuickBooks data
              <div className="h-[400px] flex flex-col items-center justify-center text-center p-8">
                <div className="text-gray-400 mb-4 animate-pulse">
                  <svg className="w-16 h-16 mx-auto mb-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Loading QuickBooks Data</h3>
                <p className="text-gray-500 text-sm">Fetching customer revenue information...</p>
              </div>
            ) : accountPerformance && accountPerformance.length === 0 ? (
              // No data state - call was successful but no data for this period
              <div className="h-[400px] flex flex-col items-center justify-center text-center p-8">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  No {aggregationType === 'value' ? 'Revenue' : 'Order'} Data
                </h3>
                <p className="text-gray-500 text-sm max-w-md">
                  {aggregationType === 'value' 
                    ? `No invoice data found in QuickBooks for the ${selectedPeriod === 'current' ? 'current' : selectedPeriod === 'previous' ? 'previous' : selectedPeriod} period.`
                    : `No orders found for the ${selectedPeriod === 'current' ? 'current' : selectedPeriod === 'previous' ? 'previous' : selectedPeriod} period.`
                  }
                </p>
                <p className="text-gray-400 text-xs mt-2">
                  Try selecting a different time period to see historical data.
                </p>
              </div>
            ) : (
              <div className="relative">
                {/* Scrollable chart container */}
                <div ref={scrollContainerRef} className="overflow-x-auto scrollbar-hide pr-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} onScroll={handleScroll}>
                <div style={{ width: `${Math.max(600, (accountPerformance?.length || 0) * 80)}px` }}>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart 
                      data={accountPerformance || []} 
                      margin={{ left: 15, right: 15, top: 20, bottom: 60 }}
                      onMouseMove={(e) => {
                        if (e.activeTooltipIndex !== undefined) {
                          setActiveBar(e.activeTooltipIndex);
                        }
                      }}
                      onMouseLeave={() => setActiveBar(null)}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        tick={<CustomXAxisTick />}
                        axisLine={false}
                        tickLine={false}
                        height={80}
                        interval={0}
                      />
                      <YAxis 
                        tickFormatter={aggregationType === 'value' ? (value) => `$${value.toLocaleString()}` : undefined}
                      />
                      <Tooltip 
                        content={(props: any) => {
                          if (props.active && props.payload && props.payload.length) {
                            const value = props.payload[0].value;
                            const label = props.label;
                            const color = props.payload[0].color;
                            
                            // Format based on aggregation type
                            let formattedValue = '';
                            if (aggregationType === 'value') {
                              formattedValue = `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                            } else if (aggregationType === 'variety') {
                              formattedValue = `${value} ${value === 1 ? 'type' : 'types'}`;
                            } else {
                              formattedValue = `${value} ${value === 1 ? 'item' : 'items'}`;
                            }
                            
                            return (
                              <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 transform transition-all duration-200 ease-in-out animate-in fade-in-0 zoom-in-95">
                                <p className="font-semibold text-gray-800">
                                  <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: color }}></span>
                                  {label}: {formattedValue}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }} 
                        cursor={false} 
                      />
                      <Bar dataKey="items" radius={[8, 8, 0, 0]} activeBar={false}>
                        {(Array.isArray(accountPerformance) ? accountPerformance : []).map((entry, index) => {
                          const baseColor = getCategoryColor(entry.name, index);
                          const fillColor = activeBar === index ? getHoverColor(baseColor) : baseColor;
                          return (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={fillColor}
                              style={{ transition: 'fill 0.2s ease-in-out', cursor: 'pointer' }}
                            />
                          );
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              {/* Left arrow indicator - clickable scroll button when not at original position - Hidden on mobile */}
              {(accountPerformance?.length || 0) > 6 && scrollPosition > 0 && (
                <button 
                  onClick={scrollChartLeft}
                  className="absolute right-12 bottom-1 p-2 hover:bg-gray-100 transition-colors duration-200 group ml-[-35px] mr-[-35px] mt-[-18px] mb-[-18px] hidden md:block"
                  data-testid="button-scroll-chart-left"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-500 group-hover:text-gray-700 transition-colors duration-200" />
                </button>
              )}
              
              {/* Right arrow indicator - clickable scroll button in bottom right - Hidden on mobile */}
              {(accountPerformance?.length || 0) > 6 && (
                <button 
                  onClick={scrollChart}
                  className="absolute right-3 bottom-1 p-2 hover:bg-gray-100 transition-colors duration-200 group pl-[8px] pr-[8px] ml-[-35px] mr-[-35px] mt-[-18px] mb-[-18px] hidden md:block"
                  data-testid="button-scroll-chart"
                >
                  <ChevronRight className="h-4 w-4 text-gray-500 group-hover:text-gray-700 transition-colors duration-200" />
                </button>
              )}
              
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Performance Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Category Distribution</CardTitle>
            <CardDescription>Breakdown of orders by product category</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={380}>
              <PieChart>
                <Pie
                  data={(() => {
                    const safeCategories = Array.isArray(categoryPerformance) ? categoryPerformance : [];
                    const totalOrders = safeCategories.reduce((sum, cat) => sum + cat.orders, 0);
                    const categories = safeCategories.map((category, index) => {
                      const percentage = (category.orders / totalOrders) * 100;
                      return {
                        name: category.name,
                        value: category.orders,
                        percentage,
                        totalValue: totalOrders,
                        color: getCategoryColor(category.name, index),
                        originalIndex: index
                      };
                    });
                    
                    // Separate categories >= 2% and < 2%
                    const majorCategories = categories.filter(cat => cat.percentage >= 2);
                    const minorCategories = categories.filter(cat => cat.percentage < 2);
                    
                    // Create "Other" category if there are minor categories
                    const result = [...majorCategories];
                    if (minorCategories.length > 0) {
                      const otherValue = minorCategories.reduce((sum, cat) => sum + cat.value, 0);
                      result.push({
                        name: 'Other',
                        value: otherValue,
                        percentage: (otherValue / totalOrders) * 100,
                        totalValue: totalOrders,
                        color: '#E7E5E4', // Light brown from our palette
                        originalIndex: -1,
                      } as any);
                    }
                    
                    return result;
                  })()}
                  cx="50%"
                  cy="45%"
                  outerRadius={140}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {(() => {
                    const safeCategories = Array.isArray(categoryPerformance) ? categoryPerformance : [];
                    const totalOrders = safeCategories.reduce((sum, cat) => sum + cat.orders, 0);
                    const categories = safeCategories.map((category, index) => {
                      const percentage = (category.orders / totalOrders) * 100;
                      return { ...category, percentage, originalIndex: index };
                    });
                    
                    const majorCategories = categories.filter(cat => cat.percentage >= 2);
                    const minorCategories = categories.filter(cat => cat.percentage < 2);
                    
                    const result = [...majorCategories];
                    if (minorCategories.length > 0) {
                      result.push({ name: 'Other', orders: minorCategories.reduce((sum, cat) => sum + cat.orders, 0), percentage: 0, originalIndex: -1 });
                    }
                    
                    return result.map((category, index) => {
                      const baseColor = category.originalIndex >= 0 ? getCategoryColor(category.name, category.originalIndex) : '#E7E5E4';
                      const fillColor = activeSlice === index ? getHoverColor(baseColor) : baseColor;
                      return (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={fillColor}
                          style={{ transition: 'fill 0.2s ease-in-out', cursor: 'pointer' }}
                          onMouseEnter={() => setActiveSlice(index)}
                          onMouseLeave={() => setActiveSlice(null)}
                        />
                      );
                    });
                  })()}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      {/* Recent Activity Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest order updates and system events</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(Array.isArray(recentOrders) ? recentOrders : []).slice(0, 5).map((order) => (
              <div key={order.id} className="flex items-start gap-4 md:gap-4 gap-2 pb-4 border-b last:border-0">
                <Badge
                  variant="secondary"
                  className={`p-2 rounded hidden md:flex ${
                    order.status === 'reviewed' ? 'bg-orange-100 text-[#ea580b] hover:bg-orange-200 border-transparent' :
                    order.status === 'cancelled' ? 'bg-red-100 text-red-800 hover:bg-red-200 border-transparent' :
                    'bg-blue-200 text-cn-brand-blue hover:bg-blue-300 border-transparent'
                  }`}
                >
                  {order.status === 'pending' ? <Clock className="h-4 w-4" /> :
                   order.status === 'reviewed' ? <Eye className="h-4 w-4" /> :
                   order.status === 'cancelled' ? <XCircle className="h-4 w-4" /> : null}
                </Badge>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">
                      Order #{order.id} - {order.account?.name || 'Unknown'}
                    </p>
                    <div className="flex flex-nowrap items-center gap-1.5">
                      <Badge
                        variant="secondary"
                        className={`rounded ${
                          order.status === 'reviewed' ? 'bg-orange-100 text-[#ea580b] hover:bg-orange-200 border-transparent' :
                          order.status === 'cancelled' ? 'bg-red-100 text-red-800 hover:bg-red-200 border-transparent' :
                          'bg-blue-200 text-cn-brand-blue hover:bg-blue-300 border-transparent'
                        }`}
                      >
                        {order.status}
                      </Badge>
                      <QbSyncedMark syncedAt={(order as any).quickbooksSyncedAt} className="flex-shrink-0" />
                      <OrderSourceBadge source={(order as any).source} className="flex-shrink-0" />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {order.totalItems} items • {new Date(order.createdAt).toLocaleString()}
                  </p>
                  {order.notes && (
                    <p className="text-sm text-gray-600 mt-1 italic">{order.notes}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}